import { Trans, useTranslation } from "react-i18next";
import type { InputSlot, PlaceholderSource, ValueKind } from "../../../api/content/authoring/selectorDecoders";
import type { StepDefinition } from "../../../api/content/authoring/stepDecoders";
import { FORM_SELECT_CLASS } from "../../../ui/styles/formStyles";
import { itemsOfKind, type AuthoringOptions } from "../draft/authoringOptions";
import { newSelector, selectorChoiceKey, selectorChoices } from "./selectorChoices";
import SelectorFields from "./SelectorFields";
import { NESTED_OPTIONS_CLASS } from "../shared/authoringStyles";
import OptionLabel from "../../../ui/forms/OptionLabel";
import FieldGroup from "../../../ui/forms/FieldGroup";
import { inputLabel } from "./inputLabel";

interface SourceEditorProps {
  definition: StepDefinition;
  /** What is being filled, as the user reads it: `{labels}` or `paragraphs`. */
  label: string;
  /** The name an input slot gets: the placeholder's or the tool input's. */
  slotName: string;
  source: PlaceholderSource | null;
  /** The slot an input source declares, if any. */
  slot: InputSlot | null;
  options: AuthoringOptions;
  /** A tool input: only selectors whose value fits; `null` for a prompt placeholder (text). */
  accepts: readonly ValueKind[] | null;
  /** Whether the source may be left empty (an optional tool input). */
  optional: boolean;
  onChange: (source: PlaceholderSource | null, slot?: InputSlot) => void;
}

type SourceMode = "selector" | "input" | "none";

/**
 * Where one placeholder or tool input gets its value (W-2, W-4, AU-4): a selector, read
 * when the step is prepared, or an input slot that a Workflow binds to an earlier step's
 * output. Exactly one source each.
 */
export default function SourceEditor({ label, slotName, definition, source, slot, options, accepts, optional, onChange }: SourceEditorProps) {
  const { t, i18n } = useTranslation();
  const inputName = source?.source === "input" ? inputLabel(definition, source.input, i18n.resolvedLanguage) : null;
  const catalogue = options.catalogue.status === "loaded" ? options.catalogue.value : null;
  const current = source?.source === "selector" ? source.selector : null;
  const choices = selectorChoices(catalogue, accepts, current);
  const mode: SourceMode = source === null ? "none" : source.source;
  const firstArtifact = itemsOfKind(options, "artifact")[0]?.itemId ?? null;
  const firstLabelList = itemsOfKind(options, "label_list")[0]?.itemId ?? null;
  const valueKinds = catalogue?.valueKinds ?? slot?.accepts ?? [];

  const chooseMode = (next: SourceMode) => {
    if (next === "none") {
      onChange(null);
    } else if (next === "input") {
      onChange({ source: "input", input: slotName }, slot ?? { accepts: accepts === null ? ["text"] : [...accepts], required: !optional });
    } else {
      const first = choices[0];
      if (first !== undefined) {
        onChange({ source: "selector", selector: newSelector(first, firstArtifact, firstLabelList) });
      }
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-3" role="radiogroup" aria-label={t("authoring.source.of", { name: label })}>
        {(["selector", "input", ...(optional ? (["none"] as const) : [])] as const).map((candidate) => (
          <OptionLabel key={candidate} size="small">
            <input type="radio" checked={mode === candidate} disabled={candidate === "selector" && choices.length === 0} onChange={() => { chooseMode(candidate); }} />
            {t(`authoring.source.modes.${candidate}`)}
          </OptionLabel>
        ))}
      </div>
      {source?.source === "selector" ? (
        <div className="space-y-2 border-l-2 pl-3">
          <select
            className={`${FORM_SELECT_CLASS} w-auto text-xs`}
            aria-label={t("authoring.source.selectorOf", { name: label })}
            value={selectorChoiceKey(source.selector)}
            onChange={(event) => {
              const choice = choices.find((candidate) => selectorChoiceKey(candidate) === event.target.value);
              if (choice !== undefined) {
                onChange({ source: "selector", selector: newSelector(choice, firstArtifact, firstLabelList) });
              }
            }}
          >
            {choices.map((choice) => (
              <option key={selectorChoiceKey(choice)} value={selectorChoiceKey(choice)}>
                {t(`authoring.selector.kinds.${choice.kind}`)} @{choice.version}
                {choice.valueKind === null ? "" : ` → ${choice.valueKind}`}
              </option>
            ))}
          </select>
          <SelectorFields selector={source.selector} options={options} label={label} onChange={(selector) => { onChange({ source: "selector", selector }); }} />
        </div>
      ) : null}
      {source?.source === "input" && slot !== null ? (
        <FieldGroup legend={t("authoring.source.slotOf", { name: label })} legendHidden className={NESTED_OPTIONS_CLASS}>
          <span className="app-text-muted"><Trans i18nKey="authoring.source.slot" components={{ slot: inputName === null ? <code>{source.input}</code> : <span>{inputName}</span> }} /></span>
          {accepts === null ? (
            valueKinds.map((kind) => (
              <OptionLabel key={kind}>
                <input
                  type="checkbox"
                  checked={slot.accepts.includes(kind)}
                  onChange={(event) => {
                    const next = event.target.checked ? [...slot.accepts, kind] : slot.accepts.filter((accepted) => accepted !== kind);
                    if (next.length > 0) {
                      onChange(source, { ...slot, accepts: next });
                    }
                  }}
                />
                <span className="font-mono">{kind}</span>
              </OptionLabel>
            ))
          ) : (
            <span>{t("authoring.source.accepts", { kinds: slot.accepts.join(", ") })}</span>
          )}
          <OptionLabel>
            <input type="checkbox" checked={slot.required} onChange={(event) => { onChange(source, { ...slot, required: event.target.checked }); }} />
            {t("authoring.source.required")}
          </OptionLabel>
          <span className="app-help-text">{t("authoring.source.slotHint")}</span>
        </FieldGroup>
      ) : null}
    </div>
  );
}
